/**
 * @fileoverview Civil & Criminal Offences workspace — shared UI types + helpers
 * @module jobs/phases/review/civilCriminalOffences/offenceShared
 *
 * Typed views over the `civilCriminalOffences/publicQueries` payloads (v.any()
 * returns), plus the badge maps. Generic formatting/cleanup helpers are reused
 * from the flagged family so the two workspaces read identically.
 */

import type { BadgeColor } from "@/ui/primitives/badge";
import type { Id } from "@cvx/_generated/dataModel";

export {
  agentText,
  fmtDate,
  fmtDateTime,
  fmtMoney,
  titleCase,
  STATUS_BADGE,
  INSOLVENCY_BASIS_LABEL,
  type HeadStatus,
  type FlaggedHistoryEntryUI as OffenceHistoryEntryUI,
  type ProposedChangeUI,
} from "../flaggedTransactions/flaggedShared";

export type OffenceHeadKey =
  | "care_diligence"
  | "good_faith"
  | "improper_position"
  | "improper_information"
  | "books_records_failure"
  | "insolvent_trading"
  | "creditor_defeating_disposition"
  | "avoiding_employee_entitlements"
  | "disqualified_management"
  | "post_appointment_dealings"
  | "rocap_failure"
  | "failure_to_assist";

export interface OffenceDecisionsUI {
  pending: number;
  actioned: number;
  dismissed: number;
  actionedValue: number;
  recoverableValue: number;
  referralCount: number;
}

export interface RespondentSummaryUI {
  respondentName: string;
  capacity?: string;
  findingCount?: number;
  totalQuantum?: number;
  banding?: string;
  referral?: boolean;
  notes?: string;
}

export interface OffenceHeadUI {
  headKey: OffenceHeadKey;
  manifestId: string;
  name: string;
  shortLabel: string;
  provision: string;
  hasQuantum: boolean;
  uiDescription: string;
  status: "live" | "stale" | "updating" | "locked" | "blocked" | "pending";
  outcome:
    | "flags_identified"
    | "no_flags"
    | "insufficient_data"
    | "blocked_missing_inputs";
  headline: string;
  reasoning: string;
  findingCount: number;
  totalValue: number;
  referralCount: number;
  confidence: number;
  reviewWindow: { start?: string; end?: string; basisNote: string };
  respondentSummaries: RespondentSummaryUI[];
  decisions: OffenceDecisionsUI;
  locked: boolean;
  staleSince: number | null;
  proposedSummary: {
    after: Record<string, unknown>;
    reasoning: string;
    proposedAt: number;
  } | null;
  lastRunAt: number;
  taskId: Id<"summarizationTasks"> | null;
  changeHistory: Array<{
    timestamp: number;
    actor: string;
    action: string;
    reason: string;
    userId?: Id<"users">;
  }>;
}

export interface OffenceWorkspaceUI {
  caseLocked: boolean;
  insolvency: { date: string | null; basis: string; note: string };
  appointmentDate: string | null;
  appointmentType: string | null;
  totals: { findingCount: number; exposure: number; referralCount: number };
  master: { taskId: Id<"summarizationTasks"> | null; running: boolean };
  caseHistory: Array<{
    timestamp: number;
    actor: string;
    action: string;
    reason: string;
    userId?: string;
  }>;
  heads: OffenceHeadUI[];
}

export interface OffenceCriminalOverlayUI {
  engaged: boolean;
  provision: string;
  faultIndicator: string;
  referralNote: string;
}

export interface OffenceFindingUI {
  _id: string;
  headKey: OffenceHeadKey;
  respondentName: string;
  respondentCapacity?: string;
  limb?: string;
  provision: string;
  summary?: string;
  conductSummary: string;
  conductPeriod?: string;
  standardAndShortfall?: string;
  knowledgeBasis?: string;
  quantum?: number;
  banding: "large" | "medium" | "small" | "none";
  risk?: "high" | "medium" | "low";
  decision: OffenceDecision;
  decisionLocked: boolean;
  rowLocked: boolean;
  isManualFinding: boolean;
  sourceDataRequired?: boolean;
  sourceDataNote?: string;
  criminalOverlay?: OffenceCriminalOverlayUI;
  defenceIndicators?: string[];
  parallelHeadFlags?: string[];
  inheritedRefs?: Array<{
    table: string;
    id?: string;
    flaggedHeadKey?: string;
    note?: string;
  }>;
  evidence?: Array<{
    documentId?: string;
    refTable?: string;
    refId?: string;
    note: string;
  }>;
  proposed?: {
    after: Record<string, unknown>;
    reasoning: string;
    proposedAt: number;
  };
  changeHistory: Array<{
    timestamp: number;
    actor: string;
    action: string;
    reason: string;
    userId?: Id<"users">;
  }>;
}

export type OffenceDecision =
  "pending" | "pursue" | "refer" | "dismiss" | "no_action";

export const OFFENCE_DECISIONS: ReadonlyArray<OffenceDecision> = [
  "pending",
  "pursue",
  "refer",
  "dismiss",
  "no_action",
];

export const OFFENCE_DECISION_BADGE: Record<
  OffenceDecision,
  { label: string; color: BadgeColor }
> = {
  pending: { label: "Pending", color: "gray" },
  pursue: { label: "Pursue", color: "emerald" },
  refer: { label: "Refer to ASIC", color: "red" },
  dismiss: { label: "Dismiss", color: "gray" },
  no_action: { label: "No action", color: "gray" },
};

/**
 * Offence heads with no insolvency element: the contravention is made out
 * without proving the company was insolvent, so the estimated insolvency date
 * is context (or, for the records head, a window anchor) rather than something
 * the claim turns on.
 *
 * Deliberately absent, as the only two heads where insolvency forms part of
 * the test: s588G insolvent trading (insolvency IS the element), and s588FDB
 * creditor-defeating dispositions, where s588FE(6B) requires one of insolvency
 * at the time, insolvency caused by the disposition, or external
 * administration within 12 months — the third limb needs no proof of
 * insolvency, but the condition itself cannot be sidestepped.
 */
export const OFFENCE_NO_INSOLVENCY_ELEMENT_NOTE: Partial<
  Record<OffenceHeadKey, string>
> = {
  care_diligence:
    "s180 has no insolvency element — the duty is breached by failing to exercise the care and diligence a reasonable person would have in the company's circumstances, and a solvent company's directors can contravene it. The date frames the decline period the conduct is assessed against (and the failure-to-monitor-solvency limb is usually the strongest claim), but it does not establish the breach; s1317H compensation requires loss to the company, not proof of insolvency.",
  good_faith:
    "s181 has no insolvency element — the duty to act in good faith in the best interests of the company and for a proper purpose applies at all times, and a breach is made out over the 6 years to appointment (s1317K) without proving insolvency. The date only frames the creditor-regarding overlay (once the company is insolvent or nearing insolvency, the company's best interests are its creditors' interests); it does not establish the breach.",
  improper_position:
    "s182 has no insolvency element — improper use of position to gain an advantage or cause detriment is assessed on its own terms over the 6 years to appointment (s1317K), whether or not the company was insolvent at the time. The case-wide date is shown for context only.",
  improper_information:
    "s183 has no insolvency element — the question is whether information obtained by virtue of the position was used improperly, assessed over the 6 years to appointment (s1317K). Insider knowledge of impending insolvency is often the evidence, but insolvency itself need not be proven. The case-wide date is shown for context only.",
  books_records_failure:
    "s286 and s344(1) have no insolvency element — the record-keeping obligation is absolute, and a director's failure to take reasonable steps contravenes it whether or not the company was insolvent. The date anchors the records-failure period and the s588E(4) presumption this head supports downstream; it does not establish the contravention.",
  avoiding_employee_entitlements:
    "s596AB has no insolvency element — the live questions are the fault element (intention under s596AB(1), or likely effect plus knowledge under s596AB(1A)) and the entitlement shortfall. Insolvency need not be proven. The case-wide date is shown for context only.",
  disqualified_management:
    "s206A has no insolvency element — the offence is managing a company while disqualified, made out by overlapping the person's management period with a period of disqualification (s206B–G). Insolvency is irrelevant to liability. The case-wide date is shown for context only.",
  post_appointment_dealings:
    "s471A and s468 have no insolvency element — the bright line is the date of appointment, from which officer powers are suspended and dispositions are void. Insolvency need not be proven. The case-wide date is shown for context only.",
  rocap_failure:
    "The ROCAP obligation has no insolvency element — it arises on appointment and is contravened by an officer failing to provide the report, in full and verified, after the due date and request. The case-wide date is shown for context only.",
  failure_to_assist:
    "s530A and s530B have no insolvency element — the obligations to deliver up books and property, attend, and give information arise on appointment. Insolvency need not be proven. The case-wide date is shown for context only.",
};

export const BANDING_BADGE: Record<
  string,
  { label: string; color: BadgeColor }
> = {
  large: { label: "Large", color: "red" },
  medium: { label: "Medium", color: "amber" },
  small: { label: "Small", color: "gray" },
  none: { label: "No Quantum", color: "gray" },
};

/** Title-cased label for the source-data-required badge. */
export const SOURCE_DATA_LABEL = "Source Data Required";

/** Band a dollar value the same way the backend does (Large/Medium/Small/None). */
export const bandForValue = (
  v: number | undefined | null,
): "large" | "medium" | "small" | "none" => {
  if (v === undefined || v === null || v === 0) return "none";
  const a = Math.abs(v);
  if (a >= 10_000) return "large";
  if (a >= 2_000) return "medium";
  return "small";
};

/** "former_director" → "Former Director". */
export const fmtCapacity = (c?: string): string =>
  c ? c.replace(/_/g, " ").replace(/\b[a-z]/g, (m) => m.toUpperCase()) : "";

/** Friendly display names for feeder heads / tables surfaced in evidence. */
export const FRIENDLY_SOURCE: Record<string, string> = {
  // flagged heads
  unfair_preferences: "Unfair Preferences",
  related_entity_preference: "Related-Entity Unfair Preferences",
  insolvent_trading: "Insolvent Trading",
  director_related: "Director-Related Transactions",
  uncommercial: "Uncommercial Transactions",
  related_entity_uncommercial: "Related-Entity Uncommercial",
  unfair_loans: "Unfair Loans",
  defeat_creditors: "Transactions to Defeat Creditors",
  creditor_defeating_disposition: "Creditor-Defeating Dispositions",
  defeat_employee_entitlements: "Defeat Employee Entitlements",
  // feeder tables
  flaggedTransactions: "Recoverable Transactions",
  flaggedHeadOutputs: "Recoverable Transactions",
  booksAndRecordsAssessment: "Books & Records",
  bankTransactionEntries: "Bank Transactions",
  corporateEntries: "Corporate",
  relatedPartyEntries: "Related Parties",
  employeeEntries: "Employment",
};

/** Map a machine head/table key to a practitioner-facing label. */
export const friendlySource = (key?: string): string => {
  if (!key) return "";
  return (
    FRIENDLY_SOURCE[key] ??
    key.replace(/_/g, " ").replace(/\b[a-z]/g, (m) => m.toUpperCase())
  );
};

/** camelCase internal field names the agent occasionally leaks into prose. */
const LEAKED_FIELDS: Record<string, string> = {
  candidateKey: "the source record",
  flaggedHeadOutputs: "Recoverable Transactions",
  flaggedTransactions: "Recoverable Transactions",
  booksAndRecordsAssessment: "Books & Records",
  corporateEntries: "Corporate",
  relatedPartyEntries: "Related Parties",
  employeeEntries: "Employment",
  bankTransactionEntries: "Bank Transactions",
};

/**
 * Replace machine tokens the agent leaves in its prose — snake_case
 * (defeat_creditors, partially_adequate) get spaced; known camelCase field
 * names (candidateKey, flaggedHeadOutputs) get human labels — so the copy reads
 * naturally.
 */
export const humaniseTokens = (s: string): string => {
  const spaced = s.replace(/\b[a-z]+(?:_[a-z]+)+\b/g, (m) =>
    m.replace(/_/g, " "),
  );
  return Object.entries(LEAKED_FIELDS).reduce(
    (out, [token, label]) =>
      out.replace(new RegExp(`\\b${token}\\b`, "g"), label),
    spaced,
  );
};

/**
 * Break a long run-on narrative line into ~2-sentence paragraphs so dense agent
 * prose reads as scannable blocks rather than a wall of text. Conservative: it
 * only acts on genuinely long lines (3+ sentences, > ~240 chars) and never
 * touches bullets or already-broken lines. Sentence boundaries only split on
 * ". " / "! " / "? " followed by a capital or quote, so statute refs ("s. 180")
 * and decimals stay intact.
 */
const paragraphiseLine = (line: string): string => {
  const trimmed = line.trim();
  if (trimmed.length < 240) return trimmed;
  const sentences = trimmed
    .replace(/([.!?])\s+(?=[A-Z"'(])/g, "$1\u0001")
    .split("\u0001")
    .map((s) => s.trim())
    .filter(Boolean);
  if (sentences.length < 3) return trimmed;
  const paras: string[] = [];
  for (let i = 0; i < sentences.length; i += 2) {
    paras.push(sentences.slice(i, i + 2).join(" "));
  }
  return paras.join("\n\n");
};

/**
 * The agent writes the conduct / standard / knowledge fields as one dense
 * paragraph even though they have clear structure — numbered limbs "(1)…(2)…",
 * "Standard:/Shortfall:" labels, and "would have: a; b; c" clause lists. This
 * rewrites that structure into the lead-sentence + bullet markup `AgentProse`
 * renders as a real list, so it's scannable. Conservative: it only breaks on
 * patterns that are unambiguous (a "(n)" enumeration followed by a capital, a
 * label at a clause boundary, a colon-introduced semicolon list). Any remaining
 * long run-on narrative is finally split into ~2-sentence paragraphs.
 */
export const structureLegalText = (raw: string): string => {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return trimmed;

  const withStructure = trimmed
    // "would have: a; b; c; and d." → a colon-introduced semicolon list → bullets.
    .replace(
      /\b((?:would|should|must|to)\b[^:.]{0,60}):\s+([^.]*?;[^.]*\.)/gi,
      (_m, lead: string, list: string) => {
        const parts = list
          .replace(/\.\s*$/, "")
          .split(/;\s*/)
          .map((p) =>
            p
              .replace(/^and\s+/i, "")
              // Strip a leading "(n)" enumeration marker inside the item.
              .replace(/^\(\s*\d+\s*\)\s*/, "")
              .trim(),
          )
          .filter(Boolean);
        if (parts.length < 2) return `${lead}: ${list}`;
        return `${lead}:\n${parts.map((p) => `- ${p}`).join("\n")}\n`;
      },
    )
    // Inline numbered limbs "(1) Foo … (2) Bar …" → bullets. Require a
    // capital after the marker so counts like "director-related (4)" are
    // left alone.
    .replace(/\s*\(\s*\d+\s*\)\s+(?=[A-Z])/g, "\n- ")
    // Belt-and-braces: strip any "(n)" left right after a bullet marker, and
    // drop orphan bullet-only lines.
    .replace(/^(\s*[-*•]\s*)\(\s*\d+\s*\)\s*/gm, "$1")
    .replace(/^\s*[-*•]\s*$/gm, "")
    // Put label lead-ins ("Standard:", "Shortfall:", "Harm:") on their own
    // line.
    .replace(
      /\s*\b(Standard|Shortfall|Harm|Defence|Knowledge|Purpose|Effect)\s*:\s+/g,
      (_m: string, label: string, offset: number) =>
        offset === 0 ? `${label}: ` : `\n\n${label}: `,
    )
    // Capitalise the first letter of every bullet item.
    .replace(
      /^(\s*[-*•]\s+)([a-z])/gm,
      (_m, lead: string, ch: string) => `${lead}${ch.toUpperCase()}`,
    );

  // Finally, split any remaining run-on narrative lines into short paragraphs,
  // leaving bullets and already-broken lines untouched.
  const paragraphised = withStructure
    .split("\n")
    .map((line) =>
      /^\s*[-*•]\s/.test(line) || line.trim() === ""
        ? line
        : paragraphiseLine(line),
    )
    .join("\n");

  return paragraphised.replace(/\n{3,}/g, "\n\n").trim();
};

/**
 * Split a "Standard: … Shortfall: …" field into its two parts so the modal can
 * render each under its own sub-header. Returns null when the text isn't in
 * that shape (then render it as a single block).
 */
export const splitStandardShortfall = (
  raw: string,
): { standard: string; shortfall: string } | null => {
  const m = /\bShortfall\s*:\s*/i.exec(raw);
  if (!m) return null;
  const standard = raw
    .slice(0, m.index)
    .replace(/^\s*Standard\s*:\s*/i, "")
    .trim();
  const shortfall = raw.slice(m.index + m[0].length).trim();
  if (!standard || !shortfall) return null;
  return { standard, shortfall };
};

const OFFENCE_OUTCOME_LABEL: Record<string, string> = {
  flags_identified: "Findings identified",
  no_flags: "No findings",
  insufficient_data: "Insufficient data",
  blocked_missing_inputs: "Blocked — missing inputs",
};

/**
 * Decision-aware outcome chip: once findings exist, the review progress is what
 * the practitioner cares about, not the agent's last assessment.
 */
export function offenceOutcomeDisplay(head: {
  outcome: OffenceHeadUI["outcome"];
  findingCount: number;
  decisions: OffenceDecisionsUI;
}): { label: string; color: BadgeColor; detail?: string } {
  const { decisions, findingCount, outcome } = head;
  const decided = decisions.actioned + decisions.dismissed;
  if (findingCount > 0) {
    if (decisions.pending === 0) {
      return {
        label: "Reviewed",
        color: "emerald",
        detail: [
          decisions.actioned > 0 ? `${decisions.actioned} actioned` : null,
          decisions.dismissed > 0 ? `${decisions.dismissed} dismissed` : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    }
    if (decided > 0) {
      return {
        label: "In review",
        color: "amber",
        detail: `${decided} of ${findingCount} reviewed`,
      };
    }
    return { label: "Findings identified", color: "amber" };
  }
  if (outcome === "no_flags") return { label: "No findings", color: "emerald" };
  return { label: OFFENCE_OUTCOME_LABEL[outcome] ?? "Not run", color: "gray" };
}
